import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  FormGroup,
  FormHelperText,
  InputLabel,
} from "@material-ui/core";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import { Button } from "@thrivetribe/gloji.ui.atoms.button";
import { ErrorMessage, Form, Formik } from "formik";
import { useEffect } from "react";
import { Helmet } from "react-helmet";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router";
import { update_signup_form_values } from "../../../../store/actions/signupFlowData";
import { FormikField } from "../../../atoms/FormikField";
import { FormikNumberInput, FormikInputOnly } from "../../../atoms/FormikInput";
import {
  ModalPageBody,
  ModalPageFooter,
  ModalPageHeader,
  ModalPageHeading,
  ModalPageWrapper,
} from "../../../organisms/ModalPage";
import { ProgrammeProps } from "../Props";
import { pageTitleSuffix } from "../../../../data/eligibilityChecker";
import { ContactNumberValidationSchema } from "../../../../data/Validation/EligibilityCheckerValidation";
import { PowerOfTen, ProgressStepEnum, setStepProgress } from "../../../../redux/slices/progressBarSlice";

interface ContactNumberProps extends ProgrammeProps {
  profilePhoneNumber?: string;
  progressPercentage?: PowerOfTen;
  gpSurgery?: string;
  gpAddress?: string;
}

export function ContactNumber(props: Readonly<ContactNumberProps>) {
  const { programmeTitle, profilePhoneNumber, progressPercentage } = props;

  const dispatch = useDispatch();
  let history = useHistory();
  const signupFormValues = useSelector(
    (state: { signupFlowData: { signupFormValues: any } }) => state.signupFlowData.signupFormValues,
  );

  const pageTitle = `Contact Number | ${programmeTitle} | ${pageTitleSuffix}`;

  const initialValues = {
    phoneNumber: signupFormValues?.phoneNumber ?? profilePhoneNumber ?? "",
    gpSurgery: signupFormValues?.gpSurgery ?? "",
    gpAddress: signupFormValues?.gpAddress ?? "",
  };

  useEffect(() => {
    if (progressPercentage) {
      dispatch(
        setStepProgress({
          progressStepEnum: ProgressStepEnum.CheckEligiblity,
          progressPercentage: progressPercentage,
        }),
      );
    }
  }, [dispatch, progressPercentage]);

  useEffect(() => {
    window.gtag("event", "page_view", {
      page_title: pageTitle,
    });
  }, [pageTitle]);

  const handleSubmit = async values => {
    const referringServiceCode = signupFormValues?.serviceCode ?? "";

    // Save values to store
    dispatch(update_signup_form_values(values));
    if (programmeTitle?.toLowerCase() === "smoke free") {
      history.push(`/almost-done`);
    } else if (referringServiceCode.toUpperCase() === "OYS") {
      //for 'One You Surrey' referrals redirect to OYS page.
      history.push("/oys-head-of-service");
    } else {
      history.push("/almost-done");
    }
  };

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={handleSubmit}
      validationSchema={ContactNumberValidationSchema}
      validateOnChange={false}
      enableReinitialize={true}
    >
      {({ isSubmitting, values, touched, errors }) => (
        <Form className="eligibility-checker-form">
          <Helmet>
            <title>{pageTitle}</title>
          </Helmet>
          <ModalPageWrapper>
            <ModalPageBody hasStickyFooter={false}>
              <ModalPageHeader
                backButtonOnClick={() => {
                  history.goBack();
                }}
              />
              <ModalPageHeading>Your contact number</ModalPageHeading>
              <FormGroup id="phoneNumber" className="eligibility-checker__form-group">
                <InputLabel htmlFor="phoneNumber" className="eligibility-checker__input-label">
                  Mobile number
                </InputLabel>
                <FormikField
                  id="phoneNumber"
                  className="eligibility-checker__text-input"
                  name="phoneNumber"
                  type="number"
                  component={FormikNumberInput}
                />
                <ErrorMessage
                  key="phoneNumber"
                  className="Mui-error"
                  name="phoneNumber"
                  component={FormHelperText}
                />
              </FormGroup>
              <Accordion square={true} elevation={0} className="eligibility-checker__accrodion">
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon fontSize="large" />}
                  aria-controls={`contact-question-explanation`}
                >
                  <span>Asking for your number already? Why do we want to know?</span>
                </AccordionSummary>
                <AccordionDetails>
                  <span>
                    We ask for your number in case one of our team needs to get in touch to support you.
                  </span>
                </AccordionDetails>
              </Accordion>
              <FormGroup id="gpSurgery" className="eligibility-checker__form-group">
                <InputLabel htmlFor="gpSurgery" className="eligibility-checker__input-label">
                  GP Surgery
                </InputLabel>
                <FormikField
                  id="gpSurgery"
                  className="eligibility-checker__text-input"
                  name="gpSurgery"
                  type="text"
                  component={FormikInputOnly}
                />
                <ErrorMessage
                  key="gpSurgery"
                  className="Mui-error"
                  name="gpSurgery"
                  component={FormHelperText}
                />
              </FormGroup>
              <FormGroup id="gpAddress" className="eligibility-checker__form-group">
                <InputLabel htmlFor="gpAddress" className="eligibility-checker__input-label">
                  GP Address
                </InputLabel>
                <FormikField
                  id="gpAddress"
                  className="eligibility-checker__text-input"
                  name="gpAddress"
                  type="text"
                  component={FormikInputOnly}
                />
                <ErrorMessage
                  key="gpAddress"
                  className="Mui-error"
                  name="gpAddress"
                  component={FormHelperText}
                />
              </FormGroup>
            </ModalPageBody>
            <ModalPageFooter hasStickyFooter={false}>
              <Button type="submit" variant="contained" color="primary" disabled={isSubmitting}>
                Continue
              </Button>
            </ModalPageFooter>
          </ModalPageWrapper>
        </Form>
      )}
    </Formik>
  );
}

export default ContactNumber;
